import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import PhotoPreviewOverlay from '../../shared/ui/PhotoPreviewOverlay'
import OrderHistory from './OrderHistory'
import InspectionHints from '../../shared/ui/InspectionHints'
import ClientInterestPanel from './ClientInterestPanel'
import BackButton from '../../shared/ui/BackButton'
import { diagnosticStyles } from '../diagnostic/diagnosticStyles'
import { orderViewStyles } from './orderViewStyles'

const statusMap = {
    ok: { label: 'Исправно', color: '#059669', bg: '#D1FAE5' },
    warning: { label: 'Некритичное', color: '#D97706', bg: '#FEF3C7' },
    critical: { label: 'Критичное', color: '#DC2626', bg: '#FEE2E2' },
    na: { label: 'Н/П', color: '#6B7280', bg: '#F3F4F6' },
}

const groupIcons = {
    'Двигатель и тех. жидкости': 'ti-engine',
    'Ходовая часть': 'ti-car',
    'Кузов и электрика': 'ti-bolt',
    'Дополнительный осмотр': 'ti-zoom-check',
}

export default function CompletedOrderView({ order, token, showBack, onBack }) {

    const { user } = useAuth()
    const [results, setResults] = useState([])
    const [intake, setIntake] = useState({ photos: [], additional_photos: [] })
    const [loading, setLoading] = useState(true)
    const [reportUrl, setReportUrl] = useState(null)
    const [shortUrl, setShortUrl] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [clientSummary, setClientSummary] = useState(order.client_summary || '')
    const [summaryConfirmed, setSummaryConfirmed] = useState(Boolean(order.summary_confirmed_at))
    const [attemptsLeft, setAttemptsLeft] = useState(3)
    const [savingSummary, setSavingSummary] = useState(false)
    const [regeneratingSummary, setRegeneratingSummary] = useState(false)
    const [confirmingSummary, setConfirmingSummary] = useState(false)
    const [sentMessage, setSentMessage] = useState(false)
    const [deliveryInfo, setDeliveryInfo] = useState({ method: order.delivery_method || null, sentAt: order.sent_at || null, sentByName: null })
    const [sendError, setSendError] = useState(false)
    const [sending, setSending] = useState(false)
    const [sendDialog, setSendDialog] = useState(null)
    const [previewPhoto, setPreviewPhoto] = useState(null)
    const isExpired = ['expired_open', 'expired_progress'].includes(order.outcome)
    const canManageReport = !isExpired && user && ['master', 'master_workshop', 'ros'].includes(user.role)
    const showClientSummary = Boolean(clientSummary) && (summaryConfirmed || user?.role === 'service_director')
    const mechanicStatement = order.mechanic_voice_transcript || order.mechanic_conclusion

    useEffect(() => {
        Promise.all([
            fetch(`/api/diagnostic/results/${order.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
            canManageReport
                ? fetch(`/api/reports/url/${order.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
                : Promise.resolve({}),
            fetch(`/api/orders/${order.id}/intake`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
        ]).then(([resultsData, reportData, intakeData]) => {
            setResults(resultsData)
            setIntake(intakeData)
            if (reportData.url) setReportUrl(reportData.url)
            if (reportData.shortUrl) setShortUrl(reportData.shortUrl)
            if (reportData.previewUrl) setPreviewUrl(reportData.previewUrl)
            if (reportData.clientSummary) setClientSummary(reportData.clientSummary)
            if (canManageReport) setSummaryConfirmed(Boolean(reportData.confirmed))
            if (Number.isInteger(reportData.attemptsLeft)) setAttemptsLeft(reportData.attemptsLeft)
            if (reportData.sent || order.is_sent) {
                setSentMessage(true)
                setDeliveryInfo({
                    method: reportData.deliveryMethod || order.delivery_method || null,
                    sentAt: reportData.sentAt || order.sent_at || null,
                    sentByName: reportData.sentByName || null,
                })
            }
            setLoading(false)
        })
    }, [order.id, token, canManageReport])

    const saveSummary = async () => {
        if (!clientSummary.trim()) return false
        setSavingSummary(true)
        try {
            const response = await fetch(`/api/reports/summary/${order.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ summary: clientSummary.trim() }),
            })
            const data = await response.json()
            if (!response.ok) throw new Error(data.message || 'Не удалось сохранить заключение')
            setClientSummary(data.client_summary)
            return true
        } catch (error) {
            alert(error.message)
            return false
        } finally {
            setSavingSummary(false)
        }
    }

    const regenerateClientSummary = async () => {
        setRegeneratingSummary(true)
        try {
            const response = await fetch(`/api/orders/${order.id}/voice/regenerate`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            })
            const data = await response.json()
            if (!response.ok) throw new Error(data.message || 'Не удалось сформировать заключение')
            setClientSummary(data.summary)
            setAttemptsLeft(data.attemptsLeft)
        } catch (error) {
            alert(error.message)
        } finally {
            setRegeneratingSummary(false)
        }
    }

    const confirmSummary = async () => {
        setConfirmingSummary(true)
        try {
            const saved = await saveSummary()
            if (!saved) return
            const response = await fetch(`/api/reports/confirm/${order.id}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            })
            const data = await response.json()
            if (!response.ok) throw new Error(data.message || 'Не удалось подтвердить заключение')
            setSummaryConfirmed(true)
            setReportUrl(data.url)
            setShortUrl(data.shortUrl || null)
            setPreviewUrl(data.previewUrl)
        } catch (error) {
            alert(error.message)
        } finally {
            setConfirmingSummary(false)
        }
    }

    const copyToClipboard = (text) => {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
    }

    const sendToClient = async (method) => {
        setSending(true)
        setSendError(false)
        try {
            const res = await fetch(`/api/reports/send/${order.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ method }),
            })
            const data = await res.json()
            if (data.success) {
                setReportUrl(data.url)
                if (data.shortUrl) setShortUrl(data.shortUrl)
                setSentMessage(true)
                setDeliveryInfo({ method: data.method, sentAt: data.sentAt, sentByName: user ? `${user.last_name} ${user.first_name}` : null })
            } else {
                setSendError(true)
            }
        } catch {
            setSendError(true)
        }
        setSendDialog(null)
        setSending(false)
    }

    const additionalPhotos = intake.additional_photos || []
    const mileageResult = intake.mileage !== null && intake.mileage !== undefined
        ? { comment: String(intake.mileage), photos: intake.photos || [] }
        : null
    const approvedAt = order.approved_at ? new Date(order.approved_at).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow' }) : null

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F5F9' }}>
            <p style={{ color: '#6B7280' }}>Загрузка...</p>
        </div>
    )

    const filteredResults = results
    const problemItems = filteredResults.filter(r => r.status === 'critical' || r.status === 'warning')
    const okItems = filteredResults.filter(r => r.status === 'ok')
    const naItems = filteredResults.filter(r => r.status === 'na')
    const okGroups = [...new Set(okItems.map(r => r.group_name))]
    const naGroups = [...new Set(naItems.map(r => r.group_name))]
    const sideText = (r, short) => r.side && r.side !== 'none' ? (short ? ' · ' : ' (') + (r.side === 'left' ? 'лев.' : 'прав.') + (short ? '' : ')') : ''
    const statusLabel = order.outcome === 'expired_open' ? 'Пропущена' : order.outcome === 'expired_progress' ? 'Брошена' : 'Завершена'
    const thumbs = (photos, alt) => photos && photos.length > 0 && (
        <div className="dg-thumbs" style={{ marginBottom: 0 }}>
            {photos.map(photo => (
                <div key={photo.id} className="dg-thumb"><img src={photo.path} alt={alt} onClick={() => setPreviewPhoto(photo.path)} /></div>
            ))}
        </div>
    )
    const sectionHead = (icon, title, count) => (
        <div className="dg-group-head">
            <span className="dg-group-icon"><i className={`ti ${icon}`} aria-hidden="true" /></span>
            <h2>{title}</h2>
            {count !== undefined && <span className="dg-group-count">{count}</span>}
        </div>
    )
    const groupedList = (groups, list, badge, muted) => (
        <div className="cv-list">
            {groups.map(group => {
                const groupItems = list.filter(r => r.group_name === group)
                return (
                    <div key={group}>
                        <div className="cv-group-row">
                            <span>{groupIcons[group] && <i className={'ti ' + groupIcons[group]} aria-hidden="true" />}{group}</span>
                            {badge}
                        </div>
                        {groupItems.map(r => (
                            <div key={r.id} className="cv-row">
                                <span className="cv-item" data-muted={muted}>{r.item_name}{sideText(r, true)}</span>
                                {!muted && r.comment && <p className="cv-comment">{r.comment}</p>}
                                {!muted && thumbs(r.photos, 'фото')}
                            </div>
                        ))}
                    </div>
                )
            })}
        </div>
    )

    return (
        <div className="dg-page">
            <style>{diagnosticStyles + orderViewStyles}</style>

            <PhotoPreviewOverlay src={previewPhoto} onClose={() => setPreviewPhoto(null)} />

            {sendDialog && (
                <div className="cv-dialog-shade">
                    <div className="cv-dialog" role="dialog" aria-modal="true">
                        {sendDialog === 'choose' ? (
                            <>
                                <span className="dg-step"><i className="ti ti-send" aria-hidden="true" /></span>
                                <h3>Как отправить клиенту?</h3>
                                <p>Выберите автоматическое сообщение через SailPlay или отправьте ссылку самостоятельно.</p>
                                <div className="cv-dialog-actions">
                                    <button type="button" className="cv-btn" data-tone="success" onClick={() => sendToClient('automatic')} disabled={sending}>{!sending && <i className="ti ti-bolt" aria-hidden="true" />}{sending ? 'Отправляем...' : 'Отправить автоматически'}</button>
                                    <button type="button" className="cv-btn" data-tone="soft" onClick={() => setSendDialog('manual')} disabled={sending}><i className="ti ti-hand-finger" aria-hidden="true" />Отправить самостоятельно</button>
                                    <button type="button" className="cv-btn" data-tone="ghost" onClick={() => setSendDialog(null)} disabled={sending}>Отмена</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className="dg-step"><i className="ti ti-link" aria-hidden="true" /></span>
                                <h3>Ручная отправка</h3>
                                <p style={{ marginBottom: '10px' }}>Скопируйте клиентскую ссылку и отправьте её удобным способом.</p>
                                <div className="cv-dialog-link">
                                    <span>{shortUrl || reportUrl}</span>
                                    <button type="button" onClick={() => copyToClipboard(shortUrl || reportUrl)}>Копировать</button>
                                </div>
                                <p className="cv-dialog-note"><i className="ti ti-info-circle" aria-hidden="true" /><span>Подтвердите только после фактической отправки ссылки клиенту. После этого повторная штатная отправка будет заблокирована.</span></p>
                                <div className="cv-dialog-row">
                                    <button type="button" className="cv-btn" data-tone="light" onClick={() => setSendDialog('choose')} disabled={sending}>Назад</button>
                                    <button type="button" className="cv-btn" data-tone="primary" onClick={() => sendToClient('manual')} disabled={sending}>{sending ? 'Сохраняем...' : 'Подтвердить отправку'}</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="dg-head">
                <div className="dg-wrap dg-head-inner">
                    <div className="dg-head-row">
                        {showBack && <BackButton onClick={onBack} />}
                        <div className="dg-head-title">
                            <p className="dg-car">{order.car_brand} {order.car_model}</p>
                            <p className="dg-sub">
                                {order.plate && <span className="dg-plate">{order.plate}</span>}
                                <span>{[order.car_year, order.client_name].filter(Boolean).join(' · ')}</span>
                                {order.client_phone && <a href={`tel:${String(order.client_phone).replace(/[^\d+]/g, '')}`}><i className="ti ti-phone" aria-hidden="true" />{order.client_phone}</a>}
                            </p>
                        </div>
                        <span className="dg-badge" style={{ background: isExpired ? '#FEE2E2' : '#D1FAE5', color: isExpired ? '#DC2626' : '#059669' }}>{statusLabel}</span>
                    </div>
                    <div className="dg-progress">
                        <div className="dg-progress-bar" data-full={!isExpired}><div style={{ width: '100%', background: isExpired ? '#DC2626' : undefined }} /></div>
                        <span>{results.length}/{results.length}</span>
                    </div>
                </div>
            </div>

            <div className="dg-wrap dg-body" style={{ gap: '12px' }}>

                {/* МАСТЕР / МЕХАНИК / САЛОН */}
                <div className="cv-info">
                    {[
                        order.master_name && { icon: 'ti-user', label: 'Мастер', value: order.master_name },
                        order.mechanic_name && { icon: 'ti-tool', label: 'Механик', value: order.mechanic_name },
                        order.salon_name && { icon: 'ti-building-store', label: 'Салон', value: order.salon_name },
                        { icon: isExpired ? 'ti-clock-x' : 'ti-circle-check', label: isExpired ? 'Закрыта автоматически' : 'Завершена', value: approvedAt || '—', tone: isExpired ? 'red' : 'green' },
                    ].filter(Boolean).map(field => (
                        <div key={field.label} className="cv-field">
                            <span className="cv-field-icon" data-tone={field.tone}><i className={`ti ${field.icon}`} aria-hidden="true" /></span>
                            <div><p>{field.label}</p><b>{field.value}</b></div>
                        </div>
                    ))}
                </div>

                {isExpired && (
                    <div className="cv-alert">
                        <i className="ti ti-alert-circle" aria-hidden="true" />
                        <span>{order.outcome === 'expired_open'
                            ? 'Заявку не взяли в работу до окончания дня. Она окончательно закрыта и доступна только для просмотра.'
                            : 'Диагностику не завершили до окончания дня. Она отмечена как брошенная, окончательно закрыта и доступна только для просмотра.'}</span>
                    </div>
                )}

                {canManageReport && !summaryConfirmed && (
                    <section className="dg-card" style={{ borderColor: '#A4BFFF' }}>
                        <div className="cv-card-title">
                            <span className="dg-step"><i className="ti ti-file-text" aria-hidden="true" /></span>
                            <div>
                                <p className="t">Заключение для клиента</p>
                                <p className="s">Проверьте текст перед созданием отчёта</p>
                            </div>
                            <span className="cv-attempts">Генераций: {attemptsLeft}</span>
                        </div>
                        <textarea
                            className="cv-textarea"
                            value={clientSummary}
                            onChange={(event) => setClientSummary(event.target.value)}
                            placeholder="Введите заключение для клиента"
                        />
                        <div className="cv-buttons">
                            <button type="button" className="cv-btn" data-tone="light" onClick={saveSummary} disabled={savingSummary || !clientSummary.trim()}>
                                <i className="ti ti-device-floppy" aria-hidden="true" />{savingSummary ? 'Сохраняем...' : 'Сохранить'}
                            </button>
                            <button type="button" className="cv-btn" data-tone="light" onClick={regenerateClientSummary} disabled={regeneratingSummary || attemptsLeft <= 0}>
                                <i className="ti ti-refresh" aria-hidden="true" />{regeneratingSummary ? 'Генерируем...' : 'Перегенерировать'}
                            </button>
                        </div>
                        <button type="button" className="cv-btn" data-tone="primary" style={{ marginTop: '8px' }} onClick={confirmSummary} disabled={confirmingSummary || savingSummary || !clientSummary.trim()}>
                            {!confirmingSummary && <i className="ti ti-check" aria-hidden="true" />}{confirmingSummary ? 'Подтверждаем...' : 'Подтвердить заключение'}
                        </button>
                    </section>
                )}

                {/* ССЫЛКА И ОТПРАВКА */}
                {reportUrl && (
                    <section className="dg-card">
                        <div className="cv-card-title">
                            <span className="dg-step"><i className="ti ti-link" aria-hidden="true" /></span>
                            <div>
                                <p className="t">Ссылки на отчёт</p>
                                <p className="s">Для клиента и внутренней проверки</p>
                            </div>
                        </div>

                        <div className="cv-link">
                            <div>
                                <p className="l">Ссылка для клиента</p>
                                <p className="u">{(shortUrl || reportUrl).replace('https://', '')}</p>
                            </div>
                            <button type="button" className="cv-copy" onClick={() => copyToClipboard(shortUrl || reportUrl)} aria-label="Скопировать ссылку для клиента">
                                <i className="ti ti-copy" aria-hidden="true" />
                            </button>
                        </div>

                        {previewUrl && (
                            <div className="cv-preview">
                                <p><i className="ti ti-shield-check" aria-hidden="true" /><span>Предпросмотр не влияет на индекс интереса</span></p>
                                <a href={previewUrl} target="_blank" rel="noreferrer"><i className="ti ti-eye" aria-hidden="true" />Открыть</a>
                            </div>
                        )}

                        {sentMessage ? (
                            <div className="cv-sent">
                                <span className="dg-step" data-done="true"><i className="ti ti-message-circle-check" aria-hidden="true" /></span>
                                <div>
                                    <b>Сообщение отправлено клиенту</b>
                                    <span>
                                        {deliveryInfo.method === 'manual' ? 'Вручную' : 'Автоматически'}
                                        {deliveryInfo.sentAt ? ` · ${new Date(deliveryInfo.sentAt).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}` : ''}
                                        {deliveryInfo.sentByName ? ` · ${deliveryInfo.sentByName}` : ''}
                                    </span>
                                </div>
                            </div>
                        ) : !canManageReport ? (user?.role === 'service_director' ? null : (
                            <button type="button" className="cv-btn" data-tone="light" disabled style={{ width: '100%', padding: '11px', marginTop: previewUrl ? 0 : '10px' }}>
                                Отправить клиенту
                            </button>
                        )) : sendError ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: previewUrl ? 0 : '10px' }}>
                                <div className="cv-send-error"><i className="ti ti-alert-triangle" aria-hidden="true" />Не удалось отправить</div>
                                <button type="button" className="cv-btn" data-tone="danger" onClick={() => setSendDialog('choose')}>
                                    <i className="ti ti-refresh" aria-hidden="true" />Повторить или отправить вручную
                                </button>
                            </div>
                        ) : (
                            <button type="button" className="cv-btn" data-tone="primary" style={{ marginTop: previewUrl ? 0 : '10px' }} onClick={() => setSendDialog('choose')} disabled={sending}>
                                {!sending && <i className="ti ti-send" aria-hidden="true" />}{sending ? 'Отправка...' : 'Отправить клиенту'}
                            </button>
                        )}
                    </section>
                )}

                {order.is_sent && <ClientInterestPanel score={order.client_interest_score} details={order.client_interest_details||{}} />}

                {/* ЗАКЛЮЧЕНИЕ */}
                {(mechanicStatement || showClientSummary) && (
                    <section className="dg-card">
                        {mechanicStatement && (
                            <div>
                                <p className="cv-quote-label"><i className="ti ti-microphone" aria-hidden="true" />Что сказал механик</p>
                                <p className="cv-quote">{mechanicStatement}</p>
                            </div>
                        )}
                        {showClientSummary && (
                            <div className="cv-final" data-pending={!summaryConfirmed} style={{ marginTop: mechanicStatement ? '14px' : 0 }}>
                                <p className="cv-quote-label" style={{ color: summaryConfirmed ? '#059669' : '#D97706' }}>
                                    <i className="ti ti-file-check" aria-hidden="true" />
                                    {summaryConfirmed ? 'Итоговое заключение для клиента' : 'Заключение для клиента · не подтверждено'}
                                </p>
                                <p className="cv-quote">{clientSummary}</p>
                            </div>
                        )}
                    </section>
                )}

                {/* ПОДСКАЗКИ AI */}
                <InspectionHints hints={order.inspection_hints} brand={order.car_brand} model={order.car_model} />

                <OrderHistory orderId={order.id} token={token} refreshKey={`${summaryConfirmed}-${deliveryInfo.sentAt || ''}`} />

                {/* 1. ПРИЁМКА */}
                {mileageResult && (
                    <div className="cv-section">
                        {sectionHead('ti-gauge', 'Приёмка')}
                        <div className="dg-card">
                            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: '#1A1A1A' }}>Пробег при приёмке</p>
                            <p style={{ margin: mileageResult.photos?.length ? '0 0 10px' : 0, fontSize: '20px', fontWeight: '700', color: '#1A1A1A' }}>
                                {mileageResult.comment} <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: '400' }}>км</span>
                            </p>
                            {thumbs(mileageResult.photos, 'одометр')}
                        </div>
                    </div>
                )}

                {/* 2. ТРЕБУЕТ ВНИМАНИЯ */}
                {problemItems.length > 0 && (
                    <div className="cv-section" data-tone="red">
                        {sectionHead('ti-alert-triangle', 'Требует внимания', problemItems.length)}
                        <div className="cv-list">
                            {problemItems.map(r => (
                                <div key={r.id} className="cv-row" data-status={r.status}>
                                    <div className="cv-row-head">
                                        <span className="cv-row-name">
                                            <i className={'ti ' + (r.status === 'critical' ? 'ti-alert-triangle' : 'ti-alert-circle')} style={{ color: statusMap[r.status] ? statusMap[r.status].color : '#6B7280' }} aria-hidden="true" />
                                            {r.item_name}{sideText(r, false)}
                                        </span>
                                        <span className="cv-pill" style={{ background: statusMap[r.status]?.bg || '#F3F4F6', color: statusMap[r.status] ? statusMap[r.status].color : '#6B7280' }}>
                                            {statusMap[r.status] ? statusMap[r.status].label : ''}
                                        </span>
                                    </div>
                                    {r.comment && <p className="cv-comment" style={{ background: 'white' }}>{r.comment}</p>}
                                    {thumbs(r.photos, 'фото')}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 3. ДОПОЛНИТЕЛЬНЫЙ ОСМОТР */}
                {additionalPhotos.length > 0 && (
                    <div className="cv-section">
                        {sectionHead('ti-zoom-check', 'Дополнительный осмотр')}
                        <div className="dg-card">
                            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '600', color: '#1A1A1A' }}>Прочие элементы</p>
                            {thumbs(additionalPhotos, 'фото')}
                        </div>
                    </div>
                )}

                {/* 4. БЕЗ ДЕФЕКТОВ */}
                {okItems.length > 0 && (
                    <div className="cv-section" data-tone="green">
                        {sectionHead('ti-circle-check', 'Без дефектов', okItems.length)}
                        {groupedList(okGroups, okItems, <span className="cv-pill" style={{ background: '#D1FAE5', color: '#059669' }}>Исправно</span>, false)}
                    </div>
                )}

                {/* 5. НЕ ПРЕДУСМОТРЕНО */}
                {naItems.length > 0 && (
                    <div className="cv-section">
                        {sectionHead('ti-minus', 'Не предусмотрено', naItems.length)}
                        {groupedList(naGroups, naItems, <span className="cv-pill" style={{ background: '#F3F4F6', color: '#9CA3AF' }}>Н/П</span>, true)}
                    </div>
                )}

                {results.length === 0 && (
                    <div className="cv-empty">Результаты диагностики не заполнены</div>
                )}

            </div>
        </div>
    )
}
